/* eslint-disable @typescript-eslint/no-explicit-any */
import { DEFAULT_AXIS_LABEL_FONT_SIZE } from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { YAXisOption } from "echarts/types/dist/shared";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import _zip from "lodash/zip";
import { EvaChartStates } from "types/factory";
import { uid } from "utils";

import { AXIS_NAME_TEXT_STYLE } from "components/multiphase-chart/constants/shared.constants";
import getXAxisPositionFromUnit from "components/multiphase-chart/util/getXAxisPositionFromUnit";

import { createChartLocks } from "../locks";
import { mpcXAxisLocks, mpcYAxisLocks } from "../locks/mpcLocks";
import { DEFAULT_Z_INDEX, STATISTICS_Z_INDEX } from "./constants";
import { getEffectiveSeriesColor } from "./getEffectiveSeriesColor";
import { productsForAxis, resolveMpcXAxisName } from "./mpcAxisLegend";
import { applyProductLegend } from "./mpcProductLegend";
import {
  buildProductStyleMap,
  buildYAxisIndexRemap,
  getLineStyleForSeries,
  getYAxisPosition,
  inferAxisProduct
} from "./mpcTranslatorHelpers";
import { updateOptionsWithTypeWellsSeries } from "./updateOptionsWithTypeWellSeries";
import {
  createLogAxisLabelFormatter,
  formatChartTitleWithSubscript,
  getDefaultXAxisLogOptions,
  getDefaultYAxisLogOptions,
  getLineSeriesMinMax,
  isStatisticsLabel
} from "./utils";

export function translateRateCumResponseToOptions(states: Partial<EvaChartStates>) {
  const {
    title,
    layout,
    series,
    typeWellSeries = [],
    logAxis,
    overrideSeries = true,
    product,
    entityKind,
    multiProduct,
    getSeriesStyle
  } = states;

  if (series.length === 0 && typeWellSeries.length === 0) {
    return {};
  }

  const primaryProducts = multiProduct?.properties?.primaryProducts || [];
  const secondaryProducts = multiProduct?.properties?.secondaryProducts || [];
  // Use Set to ensure unique products only
  const allProducts = [...new Set([...primaryProducts, ...secondaryProducts])];

  const productStyleMap = buildProductStyleMap(multiProduct);

  // Helper function to get min/max for series by axis index
  const getAxisMinMax = (seriesData, axisIndex, axisType) => {
    const filteredSeries = seriesData.filter(
      (s) => (axisType === "x" ? s.xAxisIndex ?? 0 : s.yAxisIndex ?? 0) === axisIndex
    );
    return filteredSeries.length > 0 ? getLineSeriesMinMax(filteredSeries) : null;
  };

  // Calculate min/max for default axes (axis index 0)
  const defaultAxisSeries = series.filter(
    (s) => (s.xAxisIndex ?? 0) === 0 && (s.yAxisIndex ?? 0) === 0
  );
  const { xMin, xMax, yMin, yMax } =
    defaultAxisSeries.length > 0
      ? getLineSeriesMinMax(defaultAxisSeries)
      : getLineSeriesMinMax(series); // fallback for non-multiproduct mode
  const {
    lockedXMax,
    lockedXMin,
    lockedYMax,
    lockedYMin,
    lockedYMinType,
    lockedYMaxType
  } = createChartLocks({
    type: EvaChart.RateCum,
    states
  });

  const xAxis: Partial<YAXisOption> = {
    name: formatChartTitleWithSubscript(layout.xAxis.title),
    nameGap: 27,
    min: lockedXMin,
    max: lockedXMax
  };

  const yAxis: Partial<YAXisOption> = {
    name: formatChartTitleWithSubscript(layout?.yAxis?.title) || "",
    nameGap: 43,
    nameTruncate: { maxWidth: 520.2, ellipsis: "..." },
    min: lockedYMin,
    max: lockedYMax
  };

  // TODO Multi: properly map multi axis
  let multiXAxis = null;
  let multiYAxis = null;

  // Create preset object for determining axis positions
  const axisPreset = {
    mBblPosition: multiProduct?.properties?.isMBblAxisPositionBottom ? "bottom" : "top",
    mMcfPosition: multiProduct?.properties?.isMMcfAxisPositionBottom ? "bottom" : "top"
  } as any;

  if (layout?.xAxis2?.length) {
    multiXAxis = layout.xAxis2.map((axis, i) => {
      // Calculate min/max for this specific X axis
      const axisMinMax = getAxisMinMax(series, i, "x");
      const axisXMin = axisMinMax?.xMin || xMin;
      const axisXMax = axisMinMax?.xMax || xMax;

      // Determine position based on unit in axis title
      const xAxisPosition = getXAxisPositionFromUnit(axisPreset, axis.title);

      // Infer the product for this axis from series labels
      const seriesForThisAxis = series.filter((s) => (s.xAxisIndex ?? 0) === i);
      const axisProduct = inferAxisProduct(allProducts, seriesForThisAxis, product);

      // Beside-axis product legend: a swatch beside each product on the axis
      // ({product} {swatch} {unit}), for both combined and single-product axes.
      // resolveMpcXAxisName is shared with the screenshot re-derive so the two can't drift.
      const { name: axisName, rich: legendRich } = resolveMpcXAxisName(
        axis.title,
        axis.productSegments,
        productsForAxis(allProducts, seriesForThisAxis),
        multiProduct,
        !!(multiProduct?.active && multiProduct?.properties?.showProductLegend)
      );
      // Clone — AXIS_NAME_TEXT_STYLE is a shared constant; never mutate it.
      const axisNameTextStyle: any = legendRich
        ? {
            ...AXIS_NAME_TEXT_STYLE,
            rich: { ...AXIS_NAME_TEXT_STYLE.rich, ...legendRich }
          }
        : AXIS_NAME_TEXT_STYLE;

      const { lockedXMin: axisLockedXMin, lockedXMax: axisLockedXMax } =
        multiProduct?.active
          ? mpcXAxisLocks(axisProduct, states)
          : { lockedXMin: lockedXMin, lockedXMax: lockedXMax };

      return {
        id: i,
        type: logAxis?.properties?.x?.active ? "log" : "value",
        min: axisLockedXMin,
        max: axisLockedXMax,
        name: axisName,
        nameLocation: "middle",
        nameGap: 30,
        nameTextStyle: axisNameTextStyle,
        axisLabel: { fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE },
        offset: 0,
        position: xAxisPosition,
        axisLine: {
          onZero: false
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        useY: true, // useY is managed at hd chart
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true,
          productKey: multiProduct?.active ? axisProduct : undefined,
          min: {
            value: Math.floor(axisLockedXMin ?? axisXMin?.value ?? xMin?.value ?? 0),
            type: axisXMin?.type || xMin.type,
            override: axisLockedXMin !== undefined
          },
          max: {
            value: Math.floor(axisLockedXMax ?? axisXMax?.value ?? xMax?.value ?? 0),
            type: axisXMax?.type || xMax.type,
            override: axisLockedXMax !== undefined
          }
        }
      };
    });
  }

  if (layout?.yAxis2?.length) {
    multiYAxis = layout.yAxis2.map((axis, i) => {
      // Calculate min/max for this specific Y axis
      const axisMinMax = getAxisMinMax(series, i, "y");
      const axisYMin = axisMinMax?.yMin || yMin;
      const axisYMax = axisMinMax?.yMax || yMax;

      const seriesForThisAxis = series.filter((s) => (s.yAxisIndex ?? 0) === i);

      // Infer the product for this axis from series labels
      const axisProduct = inferAxisProduct(allProducts, seriesForThisAxis, product);

      const {
        lockedYMin: axisLockedYMin,
        lockedYMax: axisLockedYMax,
        lockedYMinType: axisLockedYMinType,
        lockedYMaxType: axisLockedYMaxType
      } = multiProduct?.active
        ? mpcYAxisLocks(axisProduct, seriesForThisAxis, states)
        : {
            lockedYMin: lockedYMin,
            lockedYMax: lockedYMax,
            lockedYMinType: lockedYMinType,
            lockedYMaxType: lockedYMaxType
          };

      const axisPosition = getYAxisPosition(
        primaryProducts,
        secondaryProducts,
        seriesForThisAxis
      );

      const isLogY = logAxis?.properties?.y?.active;

      return {
        id: i,
        type: isLogY ? "log" : "value",
        min: axisLockedYMin,
        max: axisLockedYMax,
        name: formatChartTitleWithSubscript(axis.title),
        nameLocation: "middle",
        nameGap: 30,
        nameTextStyle: AXIS_NAME_TEXT_STYLE,
        axisLabel: { fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE },
        position: axisPosition,
        offset: 0,
        axisLine: {
          onZero: false
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true,
          productKey: multiProduct?.active ? axisProduct : undefined,
          min: {
            value: Math.floor(axisLockedYMin ?? axisYMin?.value ?? yMin?.value ?? 0),
            type: axisLockedYMinType ?? (axisYMin?.type || yMin.type),
            override: true
          },
          max: {
            value: Math.floor(axisLockedYMax ?? axisYMax?.value ?? yMax?.value ?? 0),
            type: axisLockedYMaxType ?? (axisYMax?.type || yMax.type),
            override: true
          }
        }
      };
    });
  }

  // 1. Remove axes not referenced by any series (the backend may send axes that
  //    have no corresponding data, e.g. position 1 unused while series use 0 & 2).
  // 2. Cap at 2 axes per side as a display safety net.
  // 3. Build a remap from original id → new array position so series yAxisIndex
  //    values stay valid after the two filtering steps.
  const yAxisIndexRemap = new Map<number, number>();
  if (multiYAxis) {
    const { filteredAxes, remap } = buildYAxisIndexRemap(multiYAxis, series);
    multiYAxis = filteredAxes;
    remap.forEach((v, k) => yAxisIndexRemap.set(k, v));
  }

  if (logAxis?.properties?.x?.active) {
    const xAxisLogOptions = getDefaultXAxisLogOptions(lockedXMin, lockedXMax);
    Object.assign(xAxis, xAxisLogOptions);

    // Apply log options to each multi X axis using per-axis lock values
    if (multiXAxis) {
      multiXAxis.forEach((axis) => {
        const axisLogOptions = getDefaultXAxisLogOptions(axis.min, axis.max);
        Object.assign(axis, axisLogOptions);
      });
    }
  }

  if (logAxis?.properties?.y?.active) {
    const yAxisLogOptions = getDefaultYAxisLogOptions(
      layout,
      lockedYMin,
      lockedYMax,
      EvaChart.RateCum,
      product,
      entityKind
    );
    Object.assign(yAxis, yAxisLogOptions);

    // Apply log options to each multi Y axis using per-axis locked values
    if (multiYAxis) {
      multiYAxis.forEach((axis) => {
        const perAxisLogOptions = getDefaultYAxisLogOptions(
          layout,
          axis.min,
          axis.max,
          EvaChart.RateCum,
          product,
          entityKind
        );
        Object.assign(axis, perAxisLogOptions);
        // Apply label formatter after min/max are finalized to suppress
        // power-of-10 labels that overlap a non-power-of-10 boundary
        axis.axisLabel = {
          ...axis.axisLabel,
          hideOverlap: true,
          formatter: createLogAxisLabelFormatter(axis.min, axis.max)
        };
      });
    }
  }

  let options = createEntityState(ChartOption.Chart, {
    chartType: EvaChart.RateCum,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    yAxis:
      layout?.yAxis2?.length > 0
        ? multiYAxis
        : {
            ...createEntityState(ChartOption.YAxis, {
              ...yAxis,
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              lock: {
                show: true, // it manages if lock should be shown or not
                min: {
                  value: Math.floor(yMin.value),
                  type: lockedYMinType ?? yMin.type,
                  override: true // it manages if lock should be overridden or not
                },
                max: {
                  value: Math.floor(yMax.value),
                  type: lockedYMaxType ?? yMax.type,
                  override: true // it manages if lock should be overridden or not
                }
              }
            })
          },
    xAxis:
      layout?.xAxis2?.length > 0
        ? multiXAxis
        : {
            ...createEntityState(ChartOption.XAxis, {
              ...xAxis,
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              useY: true, // useY is managed at hd chart
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              lock: {
                show: true,
                min: { value: Math.floor(xMin.value), type: xMin.type },
                max: { value: Math.floor(xMax.value), type: xMax.type }
              }
            })
          },
    grid: { top: 46, bottom: allProducts.length > 0 ? 70 : 46, left: 62, right: 20 },
    ...(multiProduct?.active && {
      dataZoom: [
        { type: "inside", orient: "horizontal", filterMode: "none" },
        { type: "inside", orient: "vertical", filterMode: "none" }
      ]
    }),
    ...(overrideSeries && {
      series: series.map((location, index) => {
        const { label, style, x, y, groupTitle } = location;
        const data = _zip(x, y);
        const id =
          groupTitle !== label
            ? `${groupTitle}, ${label}, ${index}, ${uid()}`
            : `${groupTitle}, ${index}, ${uid()}`;

        const isPoint = data.length === 1;
        const multiPoint = data.length > 1;
        const isStatisticsSeries = isStatisticsLabel(label);
        const { thickness, opacity } = getSeriesStyle(label, style);

        // Determine line type - forecasts keep original logic, historical data uses product-based styling
        let lineType: any;
        if (location.isForecast) {
          // Forecast series: keep original styling
          lineType = location.isComparisonForecast ? "dotted" : "dashed";
        } else {
          // Historical data: apply product-based line styles
          lineType = getLineStyleForSeries(productStyleMap, location, false, false);
        }

        const effectiveColor = getEffectiveSeriesColor(location, multiProduct);

        const lineSeries = createEntityState(ChartOption.LineSeries, {
          id,
          name: label,
          data: data,
          itemStyle: {
            color: effectiveColor
          },
          xAxisIndex: location.xAxisIndex,
          yAxisIndex: multiYAxis
            ? yAxisIndexRemap.get(location.yAxisIndex ?? 0) ?? 0
            : location.yAxisIndex,
          lineStyle: {
            width: thickness,
            color: effectiveColor,
            opacity: opacity,
            type: lineType
          },
          z: isStatisticsSeries ? STATISTICS_Z_INDEX : DEFAULT_Z_INDEX,
          ...(isPoint
            ? {
                symbolSize: 5
              }
            : {}),
          ...(isPoint ? { symbol: "circle" } : {}),
          showSymbol: !multiPoint || isPoint
        });

        return lineSeries;
      })
    })
  });

  if (typeWellSeries.length > 0) {
    options = updateOptionsWithTypeWellsSeries(options, typeWellSeries);
  }

  // The beside-axis legend needs per-product x-axes; when the layout has a
  // single x-axis (no xAxis2) fall back to the bottom legend so toggling the
  // legend on never renders nothing. No-op unless MPC + legend are active.
  if (!layout?.xAxis2?.length) {
    options = applyProductLegend(options, multiProduct);
  }

  return options;
}
